import { useState, useEffect, useMemo, createContext, useContext } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/dashboard/Sidebar";
import StatusOverview from "@/components/dashboard/StatusOverview";
import TeamBoard from "@/components/dashboard/TeamBoard";
import DeadlineView from "@/components/dashboard/DeadlineView";
import Configuration from "@/components/dashboard/Configuration";
import CardDetailModal from "@/components/dashboard/CardDetailModal";
import GlobalFilters from "@/components/dashboard/GlobalFilters";

const CardsContext = createContext(null);
export const useCards = () => useContext(CardsContext);

export default function Dashboard() {
  const [cards, setCards] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null); // For component-specific view

  const [filters, setFilters] = useState({
    vertical: "all",
    sku: "all",
    blockersOnly: false,
    sortBy: "deadline",
    dateFrom: "",
    dateTo: "",
  });

  const { token, user } = useAuth();
  const location = useLocation();
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [cardsRes, configRes] = await Promise.all([
        axios.get(`${API}/plm/cards`, { headers: authHeaders }),
        axios.get(`${API}/plm/config`, { headers: authHeaders }),
      ]);
      const cards = cardsRes.data.data || cardsRes.data;
      const config = configRes.data.data || configRes.data;
      setCards(cards);
      setConfig(config);
      // Refresh selectedCard if open
      if (selectedCard) {
        const updated = cards.find((c) => c.id === selectedCard.id);
        if (updated) setSelectedCard(updated);
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error("Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const checkAndSeed = async () => {
      try {
        const res = await axios.get(`${API}/plm/cards`, {
          headers: authHeaders,
        });
        const cardsList = res.data.data || res.data;
        if (Array.isArray(cardsList) && cardsList.length === 0) {
          await axios.post(`${API}/plm/seed`, {}, { headers: authHeaders });
          fetchData();
        }
      } catch (e) {
        /* ignore */
      }
    };
    checkAndSeed();
  }, []);

  // Apply filters and sorting
  const filteredCards = useMemo(() => {
    let result = [...cards];

    // Vertical filter
    if (filters.vertical && filters.vertical !== "all") {
      result = result.filter((c) => c.vertical === filters.vertical);
    }

    // SKU filter
    if (filters.sku && filters.sku !== "all") {
      result = result.filter((c) => c.sku === filters.sku);
    }

    // Blockers only
    if (filters.blockersOnly) {
      result = result.filter((c) => c.blocker_count > 0);
    }

    // Sort
    const getLatestDate = (card) => {
      let latest = null;
      Object.values(card.layer_statuses || {}).forEach((l) => {
        if (l.end_date && (!latest || l.end_date > latest)) latest = l.end_date;
      });
      return latest;
    };

    if (filters.sortBy === "deadline") {
      result.sort((a, b) => {
        const ad = getLatestDate(a),
          bd = getLatestDate(b);
        if (!ad && !bd) return 0;
        if (!ad) return 1;
        if (!bd) return -1;
        return ad.localeCompare(bd);
      });
    } else if (filters.sortBy === "blockers") {
      result.sort((a, b) => b.blocker_count - a.blocker_count);
    } else if (filters.sortBy === "updated") {
      result.sort((a, b) =>
        (b.updated_at || "").localeCompare(a.updated_at || ""),
      );
    }

    return result;
  }, [cards, filters]);

  const activeCards = filteredCards.filter((c) => !c.is_archived);
  const archivedCards = filteredCards.filter((c) => c.is_archived);
  const uniqueSkus = [...new Set(cards.map((c) => c.sku))].sort();

  const getPageTitle = () => {
    switch (location.pathname) {
      case "/":
        return "Status Overview";
      case "/team":
        return "Team Board";
      case "/deadlines":
        return "Deadlines";
      case "/data":
      case "/config":
        return "Configuration";
      default:
        return "Dashboard";
    }
  };

  const updateCard = async (cardId, updates) => {
    try {
      await axios.put(`${API}/plm/cards/${cardId}`, updates, {
        headers: authHeaders,
      });
      await fetchData();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save");
      return false;
    }
  };

  const updateLayerStatus = async (
    cardId,
    layer,
    status,
    blockerNote = "",
    deadline = null,
  ) => {
    try {
      await axios.put(
        `${API}/plm/cards/${cardId}/layers/status`,
        {
          layerId: layer,
          status,
          blockerReason: blockerNote,
        },
        { headers: authHeaders },
      );
      await fetchData();
      // Update selected card if open
      if (selectedCard && selectedCard.id === cardId) {
        const updated = await axios.get(`${API}/plm/cards/${cardId}`, {
          headers: authHeaders,
        });
        setSelectedCard(updated.data.data || updated.data);
      }
      toast.success("Layer updated");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update layer");
      return false;
    }
  };

  const addNote = async (cardId, note) => {
    try {
      const response = await axios.post(
        `${API}/plm/cards/${cardId}/layers/notes`,
        { layerId: "general", note },
        { headers: authHeaders },
      );
      await fetchData();
      // Update selected card
      if (selectedCard && selectedCard.id === cardId) {
        const updated = await axios.get(`${API}/plm/cards/${cardId}`, {
          headers: authHeaders,
        });
        setSelectedCard(updated.data.data || updated.data);
      }
      toast.success("Note added");
      return true;
    } catch (e) {
      console.error("Add note error:", e);
      toast.error(e.response?.data?.message || "Failed to add note");
      return false;
    }
  };

  const uploadFile = async (cardId, file) => {
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(",")[1];
        await axios.post(
          `${API}/plm/cards/${cardId}/files`,
          {
            filename: file.name,
            fileData: base64,
            fileSize: file.size,
            mimeType: file.type,
          },
          { headers: authHeaders },
        );
        await fetchData();
        // Update selected card
        if (selectedCard && selectedCard.id === cardId) {
          const updated = await axios.get(`${API}/plm/cards/${cardId}`, {
            headers: authHeaders,
          });
          setSelectedCard(updated.data.data || updated.data);
        }
        toast.success("File uploaded");
        return true;
      };
      reader.readAsDataURL(file);
    } catch (e) {
      toast.error(e.response?.data?.message || "Upload failed");
      return false;
    }
  };

  const deleteFile = async (cardId, fileId) => {
    try {
      await axios.delete(`${API}/plm/cards/${cardId}/files/${fileId}`, {
        headers: authHeaders,
      });
      await fetchData();
      toast.success("File deleted");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to delete file");
      return false;
    }
  };

  const openCardWithComponent = (card, component = null) => {
    setSelectedCard(card);
    setSelectedComponent(component);
  };

  if (loading || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <CardsContext.Provider
      value={{
        cards,
        activeCards,
        archivedCards,
        config,
        fetchData,
        updateCard,
        updateLayerStatus,
        addNote,
        uploadFile,
        deleteFile,
        selectedCard,
        setSelectedCard,
        selectedComponent,
        setSelectedComponent,
        openCardWithComponent,
        authHeaders,
        token,
        filters,
      }}
    >
      <div className="app-container" data-testid="dashboard-container">
        <Sidebar />
        <main className="main-content">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1
                className="text-2xl font-bold text-slate-900"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                {getPageTitle()}
              </h1>
              <p className="text-sm text-slate-500">
                {activeCards.length} active • {archivedCards.length} archived
              </p>
            </div>
          </div>

          <GlobalFilters
            filters={filters}
            setFilters={setFilters}
            config={config}
            uniqueSkus={uniqueSkus}
          />

          <Routes>
            <Route
              path="/"
              element={
                <StatusOverview
                  activeCards={activeCards}
                  archivedCards={archivedCards}
                />
              }
            />
            <Route
              path="/team"
              element={
                <TeamBoard
                  activeCards={activeCards}
                  archivedCards={archivedCards}
                />
              }
            />
            <Route
              path="/deadlines"
              element={
                <DeadlineView
                  activeCards={activeCards}
                  archivedCards={archivedCards}
                />
              }
            />
            <Route path="/data" element={<Configuration />} />
            <Route path="/config" element={<Configuration />} />
          </Routes>

          {selectedCard && (
            <CardDetailModal
              card={selectedCard}
              initialComponent={selectedComponent}
              onClose={() => {
                setSelectedCard(null);
                setSelectedComponent(null);
              }}
            />
          )}
        </main>
      </div>
    </CardsContext.Provider>
  );
}
